import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAuditLogs } from '@/store/slices/adminSlice';
import { Clock, User, FileText, CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';
import Badge from '@/components/common/Badge';
import Modal from '@/components/common/Modal';

// Helper to pick icons/colors based on action
const getActionMeta = (action, severity) => {
  if (severity === 'error') return { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' };
  if (severity === 'warning') return { icon: AlertCircle, color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30' };

  const lowerAction = action.toLowerCase();
  if (lowerAction.includes('approve') || lowerAction.includes('publish') || lowerAction.includes('grant') || lowerAction.includes('promote') || lowerAction.includes('create')) {
    return { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30' };
  }
  if (lowerAction.includes('reject') || lowerAction.includes('revoke') || lowerAction.includes('suspend') || lowerAction.includes('hide') || lowerAction.includes('archive') || lowerAction.includes('delete')) {
    return { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' };
  }
  if (lowerAction.includes('edit') || lowerAction.includes('update')) {
    return { icon: AlertCircle, color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30' };
  }
  return { icon: Info, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' };
};

const EntityAuditHistory = ({ entityId, entityType, actorId }) => {
  const dispatch = useDispatch();
  const [localLogs, setLocalLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMetadata, setSelectedMetadata] = useState(null);

  useEffect(() => {
    if (!entityId && !entityType && !actorId) return;
    
    setLoading(true);
    
    const params = { limit: 50 };
    if (actorId) {
      params.actor_id = actorId;
    } else {
      params.entity_id = entityId;
      params.entity_type = entityType;
    }

    // Use the generic thunk, but we can also handle local state here to not overwrite the global dashboard logs
    // since we might view entity detail while having dashboard loaded.
    // It's safer to use the promise from dispatch directly:
    dispatch(fetchAuditLogs(params))
      .unwrap()
      .then((res) => {
        setLocalLogs(res.data?.logs || res.data || []);
      })
      .catch((err) => {
        setError(err.message || "Failed to load audit history");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [entityId, entityType, actorId, dispatch]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-4 rounded-lg text-sm">
        {error}
      </div>
    );
  }

  if (localLogs.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <FileText className="w-8 h-8 mx-auto text-gray-400 mb-2" />
        <p className="text-gray-500 dark:text-gray-400">No audit history found for this entity.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative border-l-2 border-gray-200 dark:border-gray-700 ml-3 pl-6 pb-2 space-y-6">
        {localLogs.map((log) => {
          const { icon: ActionIcon, color, bg } = getActionMeta(log.action, log.severity);
          return (
            <div key={log.id} className="relative">
              {/* Timeline dot */}
              <div className={`absolute -left-[35px] top-1 w-6 h-6 rounded-full ${bg} flex items-center justify-center ring-4 ring-white dark:ring-gray-900`}>
                <ActionIcon className={`w-3.5 h-3.5 ${color}`} />
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <Badge tone={log.severity === 'error' ? 'danger' : log.severity === 'warning' ? 'warning' : 'info'} className="capitalize">
                      {log.action?.split(/[\._]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(log.timestamp).toLocaleString('en-IN', {
                      day: 'numeric', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </div>
                </div>
                
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {actorId ? (
                    <>
                      {'Action on '}
                      <span className="font-semibold text-gray-900 dark:text-gray-100 capitalize">
                        {log.entity?.type || 'Unknown'} {log.entity?.id ? `(${log.entity.id.substring(0, 8)}...)` : ''}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {log.actor?.name || 'System'}
                      </span>
                      {' performed this action.'}
                    </>
                  )}
                </div>
                
                {/* Structured Metadata Output rather than raw log.summary */}
                {log.metadata && Object.keys(log.metadata).length > 0 && (
                  <div className="mt-3 flex flex-col gap-2">
                    {/* State Transitions */}
                    {log.metadata.from && log.metadata.to && (
                      <div className="flex items-center gap-2 font-mono text-xs">
                        <span className="text-gray-500 font-sans font-medium">Transition:</span>
                        <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded-md text-gray-700 dark:text-gray-300">
                          {log.metadata.from}
                        </span>
                        <span className="text-gray-400">➔</span>
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded-md font-medium">
                          {log.metadata.to}
                        </span>
                      </div>
                    )}
                    
                    {/* Reason Block */}
                    {log.metadata.reason && (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-gray-500">Reason:</span>
                        <div className="text-sm text-gray-600 dark:text-gray-400 italic bg-white dark:bg-gray-900/50 border-l-4 border-gray-300 dark:border-gray-600 p-2 rounded-r-md" title={log.metadata.reason}>
                          "{log.metadata.reason}"
                        </div>
                      </div>
                    )}
                    
                    {/* Changed Fields / Diffs */}
                    {log.metadata.metadata?.diffs ? (
                      <div className="flex flex-col gap-1.5 mt-2">
                        <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Changes</span>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(log.metadata.metadata.diffs).map(([field, {before, after}]) => (
                            <div key={field} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-[10px] flex flex-col min-w-[120px] max-w-[220px]">
                              <div className="bg-gray-50 dark:bg-gray-700/50 px-1.5 py-0.5 border-b border-gray-200 dark:border-gray-700 font-mono text-gray-600 dark:text-gray-300 font-semibold truncate">{field}</div>
                              <div className="px-1.5 py-1 flex flex-col gap-0.5">
                                <div className="text-red-600 dark:text-red-400 line-through truncate" title={JSON.stringify(before)}>{typeof before === 'object' ? JSON.stringify(before) : String(before)}</div>
                                <div className="text-green-600 dark:text-green-400 truncate" title={JSON.stringify(after)}>{typeof after === 'object' ? JSON.stringify(after) : String(after)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : log.metadata.metadata?.changedFields && (
                      <div className="flex flex-wrap gap-1 mt-2 items-center">
                        <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mr-1">Updated</span>
                        {log.metadata.metadata.changedFields.map(f => (
                          <span key={f} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800/80 rounded text-gray-600 dark:text-gray-300 text-[10px] font-mono">{f}</span>
                        ))}
                      </div>
                    )}
                    
                    {/* Always allow viewing the raw payload */}
                    <div className="mt-2">
                      <button 
                        onClick={() => setSelectedMetadata(log.metadata)}
                        className="text-[10px] uppercase tracking-wider text-blue-600 dark:text-blue-400 hover:text-blue-800 font-bold transition-colors"
                      >
                        View Full Payload
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Modal 
        open={!!selectedMetadata} 
        onClose={() => setSelectedMetadata(null)}
        title="Details"
      >
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md border border-gray-200 dark:border-gray-700 overflow-x-auto divide-y divide-gray-200 dark:divide-gray-700">
           <div className="space-y-3">
             {selectedMetadata && Object.entries(selectedMetadata).map(([key, value]) => (
                <div key={key} className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 last:border-0 border-b border-gray-200 dark:border-gray-700">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 capitalize">
                    {key.replace(/_/g, ' ')}
                  </div>
                  <div className="md:col-span-2 text-sm text-gray-900 dark:text-gray-100 font-mono break-all">
                    {key === 'metadata' && value?.diffs ? (
                      <div className="space-y-4">
                        {value.changedFields && (
                          <div className="text-xs text-gray-500">
                            <span className="font-semibold text-gray-700 dark:text-gray-300 mr-2">Updated fields:</span>
                            {value.changedFields.join(', ')}
                          </div>
                        )}
                        <div className="space-y-2 mt-2">
                          {Object.entries(value.diffs).map(([f, diff]) => (
                            <div key={f} className="text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-900/50 p-2 flex flex-col gap-1 shadow-sm">
                              <span className="font-bold text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700 pb-1 mb-1">{f}</span>
                              <div className="text-red-600 bg-red-50 dark:bg-red-900/10 px-1.5 py-0.5 rounded break-all">- {typeof diff.before === 'object' ? JSON.stringify(diff.before) : String(diff.before)}</div>
                              <div className="text-green-600 bg-green-50 dark:bg-green-900/10 px-1.5 py-0.5 rounded break-all">+ {typeof diff.after === 'object' ? JSON.stringify(diff.after) : String(diff.after)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (value === null || value === undefined || value === '') 
                      ? <span className="text-gray-400 italic">Not provided</span> 
                      : typeof value === 'object'
                        ? JSON.stringify(value, null, 2)
                        : String(value)}
                  </div>
                </div>
             ))}
           </div>
        </div>
      </Modal>
    </div>
  );
};

export default EntityAuditHistory;
